import { getState } from '../state/store.js';
import { getSelectedSite } from '../sites/sites.js';
import { loadRecentTransactions } from '../transactions/transactions.js';
import { parseFile, classifyRows, importRows } from '../upload/upload.js';
import { openFormSheet, closeFormSheet } from './modals.js';
import { money, formatDate } from './format.js';
import { loadProjectHealth, loadDashboardTransactions } from '../dashboard/projectHealth.js';

function escapeHtml(s) {
  return String(s ?? '').replace(/[&<>"']/g, (m) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#039;'
  }[m]));
}

const STATUS_LABEL = { valid: 'VALID', invalid: 'INVALID', duplicate: 'POSSIBLE DUPLICATE' };
const STATUS_CLASS = { valid: 'green', invalid: 'red', duplicate: 'amber' };

function previewRowHtml(row, index) {
  const checkbox = row.status === 'duplicate'
    ? `<input type="checkbox" class="dupImportCheck" data-index="${index}">`
    : '';
  return `
    <tr>
      <td>${formatDate(row.entry_date) || '—'}</td>
      <td>${escapeHtml(row.type || '—')}</td>
      <td>${escapeHtml(row.description || '—')}</td>
      <td>${escapeHtml(row.category || '—')}</td>
      <td class="right">${row.amount ? money(row.amount) : '—'}</td>
      <td>${escapeHtml(row.payment_mode || '—')}</td>
      <td>
        <span class="statusPill ${STATUS_CLASS[row.status]}">${STATUS_LABEL[row.status]}</span>
        ${row.status === 'invalid' ? `<small class="invalidReason">${escapeHtml(row.reasons.join(', '))}</small>` : ''}
        ${checkbox}
      </td>
    </tr>
  `;
}

export function openUploadForm() {
  const state = getState();
  if (!state.selectedSiteId) {
    alert('Select a site before importing.');
    return;
  }

  const body = document.createElement('div');
  body.className = 'uploadFormBody';
  body.innerHTML = `
    <div class="formSiteBadge">Site: <b>${escapeHtml(getSelectedSite()?.name || '')}</b></div>
    <label>Statement File (PDF, CSV or XLSX)<input id="uploadFileInput" type="file" accept=".pdf,.csv,.txt,.xlsx,.xls"></label>
    <div id="uploadError" class="formError" hidden></div>
    <div id="uploadPreviewWrap"></div>
  `;

  let classifiedRows = [];
  let fileName = '';

  const fileInput = body.querySelector('#uploadFileInput');
  const errorBox = body.querySelector('#uploadError');
  const previewWrap = body.querySelector('#uploadPreviewWrap');

  function renderPreview() {
    const validCount = classifiedRows.filter((r) => r.status === 'valid').length;
    const invalidCount = classifiedRows.filter((r) => r.status === 'invalid').length;
    const dupCount = classifiedRows.filter((r) => r.status === 'duplicate').length;

    previewWrap.innerHTML = `
      <div class="importSummary">
        <span>VALID: <b class="green">${validCount}</b></span>
        <span>INVALID: <b class="red">${invalidCount}</b></span>
        <span>POSSIBLE DUPLICATES: <b class="amber">${dupCount}</b></span>
      </div>
      <div class="importPreviewTableWrap">
        <table class="importPreviewTable">
          <thead><tr><th>Date</th><th>Type</th><th>Description</th><th>Category</th><th class="right">Amount</th><th>Payment Mode</th><th>Status</th></tr></thead>
          <tbody>${classifiedRows.map(previewRowHtml).join('')}</tbody>
        </table>
      </div>
      <div id="importFinalError" class="formError" hidden></div>
      <button class="wideBtn" id="importConfirmBtn" type="button">Import 0 Records</button>
    `;

    const importBtn = previewWrap.querySelector('#importConfirmBtn');
    function updateImportCount() {
      const checkedDupIndexes = new Set(
        [...previewWrap.querySelectorAll('.dupImportCheck:checked')].map((el) => Number(el.dataset.index))
      );
      const toImport = classifiedRows.filter((r, i) => r.status === 'valid' || (r.status === 'duplicate' && checkedDupIndexes.has(i)));
      importBtn.textContent = `Import ${toImport.length} Record${toImport.length === 1 ? '' : 's'}`;
      importBtn.disabled = toImport.length === 0;
      return toImport;
    }
    previewWrap.querySelectorAll('.dupImportCheck').forEach((cb) => cb.addEventListener('change', updateImportCount));
    updateImportCount();

    importBtn.addEventListener('click', async () => {
      const finalErrorBox = previewWrap.querySelector('#importFinalError');
      finalErrorBox.hidden = true;
      const toImport = updateImportCount();
      if (!toImport.length) return;

      importBtn.disabled = true;
      importBtn.textContent = 'Importing…';

      const result = await importRows(toImport, getState().selectedSiteId, fileName);

      if (!result.ok) {
        finalErrorBox.textContent = result.error;
        finalErrorBox.hidden = false;
        importBtn.disabled = false;
        importBtn.textContent = `Import ${toImport.length} Record${toImport.length === 1 ? '' : 's'}`;
        return; // do NOT show success, do NOT close, until Supabase confirms
      }

      await Promise.all([
        loadRecentTransactions(getState().selectedSiteId),
        loadProjectHealth(),
        loadDashboardTransactions()
      ]);
      previewWrap.innerHTML = `
        <div class="importSuccess">✓ Imported ${result.count} record${result.count === 1 ? '' : 's'}.</div>
        <button class="wideBtn" id="importDoneBtn" type="button">Done</button>
      `;
      previewWrap.querySelector('#importDoneBtn').addEventListener('click', closeFormSheet);
    });
  }

  fileInput.addEventListener('change', async () => {
    const file = fileInput.files?.[0];
    if (!file) return;
    errorBox.hidden = true;
    previewWrap.innerHTML = '<div class="empty">Parsing…</div>';
    fileName = file.name;
    try {
      const rows = await parseFile(file);
      classifiedRows = classifyRows(rows);
      renderPreview();
    } catch (err) {
      previewWrap.innerHTML = '';
      errorBox.textContent = err.message || String(err);
      errorBox.hidden = false;
    }
  });

  openFormSheet('Upload Statement', body);
}
